import { computed, defineComponent } from 'vue'
import type { IFileDropProps } from './types'
import Icon from '../icon'
import { useFileSelect } from '@/utils/hooks/useFileSelect'
import { EAttachmentFileType, type IAttachment, type ISavedDocument } from 'findef'
import { FORM_INPUT_FIELD_PROPS } from '../form/input/types'
import { Grid } from '../grid'
import { getAttachmentIconSource } from '@/utils/attachment/icon'
import { FindexClient } from '@/integrations/findex'
import FileIcon from 'assets/img/svg/icons/file-copy2.svg?url'
import ProgressBar from '../progress-bar'
import CancelIcon from 'assets/img/svg/icons/close_circle.svg?url'
import TrashIcon from 'assets/img/svg/icons/trash-outline.svg?url'
import CheckIcon from 'assets/img/svg/icons/checked.svg?url'
import Circle from '../circle'
import { clip } from '@/utils/string'
import { formatBytes } from '@/utils/formatting/memory'
import { useTweenedNumber } from '@/utils/hooks/useTweenedNumber'
import { delayedRef } from '@/utils/reactive/delayedRef'
import { Spinner } from '../spinner'

type UploadTransaction = {
  name?: string
  progress?: number
  inProgress?: boolean
  totalSize?: number
  attachment?: ISavedDocument<IAttachment>
}

const UploadedFile = defineComponent<{
  onCancel: () => void | Promise<void>
  onDelete: (attachment: ISavedDocument<IAttachment>) => void | Promise<void>
  transaction?: any
  attachment: ISavedDocument<IAttachment>
}>(
  (props) => {
    const attachment = computed(() => props.attachment)

    const iconSource = computed(() =>
      attachment.value ? getAttachmentIconSource(attachment.value) : undefined
    )
    const imageSource = computed(() =>
      attachment.value && attachment.value.fileType === EAttachmentFileType.IMAGE
        ? FindexClient.urlForAttachment(attachment.value)
        : undefined
    )

    const realProgress = computed(() => (props.transaction?.progress || 1) * 100)

    const delayedProgress = delayedRef<number>(realProgress, 90)

    const progress = useTweenedNumber({
      value: delayedProgress,
      duration: 0.15
    })

    const name = computed(() => clip(props.attachment?.name || props.transaction?.name || '?', 24))
    const filesize = computed(() => formatBytes(props.attachment?.sizeBytes || props.transaction?.totalSize || 0))
    const isInProgress = computed(() => props.transaction?.inProgress || false)
    const finished = computed(() => isInProgress.value === false && progress.value > 0.9941291)

    const handleCancel = () => {
      if (props.onCancel) {
        props.onCancel()
      }
    }

    const handleDelete = () => {
      const attach = attachment.value
      if (props.onDelete && attach) {
        props.onDelete(attach)
      }
    }

    return () => {
      return (
        <Grid
          columns="12rem  max-content"
          class="h-16"
          justifyContent="space-between"
          gap="2rem"
        >
          <Grid noDefaultLayout alignItems="center" columns="max-content max-content" gap="0.5rem">
            <div class="w-10 h-10 bg-gray-100 p-1 text-gray-400 flex items-center content-center justify-center overflow-hidden">
              {imageSource.value ? (
                <img
                  style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover'
                  }}
                  src={imageSource.value}
                />
              ) : iconSource.value ? (
                <Icon src={iconSource.value} fill="currentColor" />
              ) : (
                <Icon src={FileIcon} fill="currentColor" />
              )}
            </div>
            <Grid noDefaultLayout rows="max-content max-content">
              <div class="text-gray-900 text-sm font-medium font-['Inter']">{name.value}</div>
              <div class="text-gray-500 text-xs font-normal font-['Inter']">{filesize.value}</div>
            </Grid>
          </Grid>

          <Grid columns="max-content max-content" gap="0.5rem" class="text-gray-500">
            {finished.value ? (
              <>
                <Grid noDefaultLayout alignItems="center">
                  <Circle size="xs" variant="emerald600">
                    <Icon src={CheckIcon} stroke="white" size={'0.6rem'} />
                  </Circle>
                </Grid>
                <Grid
                  noDefaultLayout
                  alignItems="center"
                  onClick={handleDelete}
                  class="cursor-pointer"
                >
                  <Icon src={TrashIcon} fill="currentColor" size="1em" />
                </Grid>
              </>
            ) : (
              <>
                <Grid noDefaultLayout style={{ minWidth: '8rem' }} alignItems="center">
                  {
                    progress.value <= 0 ? <Spinner size={6}/> : <ProgressBar withBackground size="xs" value={progress.value} showPercentage />
                  }
                </Grid>
                <Grid
                  noDefaultLayout
                  alignItems="center"
                  onClick={handleCancel}
                  class="cursor-pointer"
                >
                  <Icon src={CancelIcon} fill="currentColor" size="1em" />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      )
    }
  },
  { props: ['onCancel', 'onDelete', 'transaction', 'attachment'] }
)

const FileDrop = defineComponent<IFileDropProps>({
  setup(props) {
    const fileSelect = useFileSelect()
    const uploads = ref<ISavedDocument<IAttachment>[]>([]);

    watch(() => props.value, (newValue) => {
      if (newValue) {
        uploads.value = Array.isArray(newValue) ? [...newValue] : [newValue]
      }
    });

    const uploadFiles = async (_files: File[]): Promise<ISavedDocument<IAttachment>[]> => {
      const attachments: any[] = []

      const dataRef = props.dataRef
      if (dataRef) {
        dataRef.setValue([...(uploads.value || []), ...attachments])
        uploads.value = [...(uploads.value || []), ...attachments]
        console.log("Set value", [...(uploads.value || []), ...attachments])
        console.log("Did set dataref", uploads.value, attachments, [...uploads.value, ...attachments])
        console.log("dataRef value", dataRef.value)
      }

      console.log("Return:", attachments)

      return attachments
    }

    const transactions = computed((): UploadTransaction[] => {
      return []
    })

    const onReceiveFiles = async (files: File[]) => {
      await uploadFiles(files)
    }

    const handleClick = async () => {
      const fileList = await fileSelect.select()
      if (!fileList) return
      const files = Array.from(fileList)
      if (files.length <= 0) return
      onReceiveFiles(files)
    }

    const handleDragOver = (ev: DragEvent) => {
      ev.preventDefault()
    }

    const getFiles = (ev: DragEvent): Array<File> | null => {
      const data = ev.dataTransfer
      if (!data) return null
      if (data.files) {
        return Array.from(data.files)
      }
      if (data.items) {
        return Array.from(data.items)
          .map((it) => it.getAsFile())
          .filter((it) => !!it) as File[]
      } else {
        return Array.from(data.files)
      }
    }

    const handleDrop = async (ev: DragEvent) => {
      ev.preventDefault()
      const files = getFiles(ev)
      if (!files || files.length <= 0) return
      onReceiveFiles(files)
    }

    const handleCancel = (name: string) => {
      console.log(`handleCancel: ${name}`)
    }

    const handleDelete = async (attachment: ISavedDocument<IAttachment>, _name: string) => {
      const dataRef = props.dataRef
      if (dataRef) {
        dataRef.setValue(Array.isArray(dataRef.value.value) ? dataRef.value.value.filter((att: ISavedDocument<IAttachment>) => att._id !== attachment._id) : [])
      }
    }

    return () => {
      return (
        <Grid noDefaultLayout rows="max-content max-content" class="w-full h-full" gap="1rem">
          <div
            onDragover={handleDragOver}
            onDrop={handleDrop}
            onClick={handleClick}
            class="w-full h-40 bg-gray-100 rounded-lg border-2 border-gray-200 flex-col justify-center items-center gap-2.5 inline-flex border-dashed cursor-pointer hover:opacity-80"
          >
            <div class="flex-col justify-center items-center gap-2.5 flex text-gray-500">
              <div class="w-6 h-6 relative">
                <Icon src={async () => await import("assets/img/svg/icons/upload2.svg?url")} size={'1.5em'} fill="currentColor" />
              </div>
              <span class="font-bold text-sm">
                Click to upload or drag and drop
              </span>
              <div class="text-xs text-center font-normal">Max 10MB</div>
            </div>
          </div>
          <div>
            {uploads.value && uploads.value.map((it) => {
              const name = it.name || '?'
              return (
                <UploadedFile
                  onCancel={() => handleCancel(it.name)}
                  onDelete={(attachment) => handleDelete(attachment, it.name)}
                  attachment={it}
                  key={`uploaded-file-${name}`}
                />
              )
            })}
            {transactions.value && transactions.value.map((it) => {
              const name = it.attachment?.name || it.name || '?'
              return (
                <UploadedFile
                  onCancel={() => handleCancel(it.name || '')}
                  onDelete={(attachment) => handleDelete(attachment, it.name || '')}
                  transaction={it}
                  attachment={it.attachment!}
                  key={`uploaded-file-${name}`}
                />
              )
            })}
          </div>
        </Grid>
      )
    }
  }
})

FileDrop.props = ['options', ...FORM_INPUT_FIELD_PROPS]

export default FileDrop
